import { ArrowDownIcon, ArrowUpIcon, ChevronsUpDownIcon, DownloadIcon } from "lucide-react";
import { useMemo, useState, type ReactNode } from "react";

import { Button } from "~/components/ui/button";
import { cn } from "~/lib/utils";

import {
  downloadCsv,
  FILTER_ALL,
  queryOkrsTable,
  stringifyCsv,
  uniqueFilterOptions,
  type OkrsFilterSpec,
  type SortState,
} from "./tableQuery";

export type OkrsColumn<T> = {
  className?: string;
  csvValue?: (row: T) => string;
  headerClassName?: string;
  key: string;
  label: string;
  render: (row: T) => ReactNode;
  sortable?: boolean;
  sortValue?: (row: T) => string | number;
};

export function OkrsTable<T>(props: {
  columns: Array<OkrsColumn<T>>;
  emptyMessage?: string;
  exportFilename?: string;
  filters?: Array<OkrsFilterSpec<T>>;
  onRowClick?: ((row: T) => void) | undefined;
  pageSize?: number;
  rowKey: (row: T) => string;
  rows: T[];
  searchPlaceholder: string;
  searchText: (row: T) => string;
  toolbar?: ReactNode;
}) {
  const filterSpecs = props.filters;
  const [search, setSearch] = useState("");
  const [filterValues, setFilterValues] = useState<Record<string, string>>(() =>
    Object.fromEntries((filterSpecs ?? []).map((filter) => [filter.id, FILTER_ALL])),
  );
  const [sort, setSort] = useState<SortState>(null);
  const [page, setPage] = useState(1);

  const result = useMemo(() => {
    const specs = filterSpecs ?? [];
    return queryOkrsTable({
      filters: specs.map((filter) => ({
        getValue: filter.getValue,
        value: filterValues[filter.id] ?? FILTER_ALL,
      })),
      getSortValue: (row, key) => {
        const column = props.columns.find((item) => item.key === key);
        return column?.sortValue?.(row) ?? "";
      },
      page,
      rows: props.rows,
      search,
      searchText: props.searchText,
      sort,
      ...(props.pageSize === undefined ? {} : { pageSize: props.pageSize }),
    });
  }, [
    filterSpecs,
    filterValues,
    page,
    props.columns,
    props.pageSize,
    props.rows,
    props.searchText,
    search,
    sort,
  ]);

  const onSearchChange = (value: string) => {
    setSearch(value);
    setPage(1);
  };

  const onFilterChange = (id: string, value: string) => {
    setFilterValues((current) => ({ ...current, [id]: value }));
    setPage(1);
  };

  const onSort = (key: string) => {
    setSort((current) => {
      if (current?.key !== key) return { direction: "asc", key };
      if (current.direction === "asc") return { direction: "desc", key };
      return null;
    });
    setPage(1);
  };

  const onExport = () => {
    if (!props.exportFilename) return;
    const header = props.columns.map((column) => column.label);
    const rows = result.filtered.map((row) =>
      props.columns.map((column) => {
        if (column.csvValue) return column.csvValue(row);
        const sortValue = column.sortValue?.(row);
        return sortValue === undefined ? "" : String(sortValue);
      }),
    );
    downloadCsv(props.exportFilename, stringifyCsv(header, rows));
  };

  return (
    <div className="overflow-hidden rounded-xl border border-border bg-card">
      <div className="flex flex-col gap-2 border-b border-border/70 p-3 sm:flex-row sm:flex-wrap sm:items-center">
        <input
          aria-label="Search"
          className="h-8 w-full rounded-lg border border-input bg-background px-2.5 text-sm text-foreground shadow-xs outline-none sm:max-w-64"
          onChange={(event) => onSearchChange(event.target.value)}
          placeholder={props.searchPlaceholder}
          type="search"
          value={search}
        />
        {(filterSpecs ?? []).map((filter) => {
          const options = filter.options ?? uniqueFilterOptions(props.rows, filter.getValue);
          const value = filterValues[filter.id] ?? FILTER_ALL;
          return (
            <select
              aria-label={filter.label}
              className="h-8 rounded-lg border border-input bg-background px-2.5 text-sm text-foreground shadow-xs outline-none sm:w-40"
              key={filter.id}
              onChange={(event) => onFilterChange(filter.id, event.target.value)}
              value={value}
            >
              <option value={FILTER_ALL}>{filter.allLabel}</option>
              {options.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          );
        })}
        {props.toolbar || props.exportFilename ? (
          <div className="flex flex-wrap items-center gap-3 sm:ml-auto">
            {props.toolbar}
            {props.exportFilename ? (
              <Button
                disabled={result.total === 0}
                onClick={onExport}
                size="xs"
                type="button"
                variant="outline"
              >
                <DownloadIcon />
                Export CSV
              </Button>
            ) : null}
          </div>
        ) : null}
      </div>

      <div className="overflow-x-auto">
        <table className="w-full min-w-[640px] border-collapse text-sm">
          <thead>
            <tr className="border-b border-border/70 text-left text-xs text-muted-foreground">
              {props.columns.map((column) => (
                <th
                  key={column.key}
                  className={cn("px-3 py-2 font-medium whitespace-nowrap", column.headerClassName)}
                >
                  {column.sortable ? (
                    <button
                      aria-label={`Sort by ${column.label}`}
                      className="inline-flex items-center gap-1 hover:text-foreground"
                      onClick={() => onSort(column.key)}
                      type="button"
                    >
                      {column.label}
                      <SortIcon
                        active={sort?.key === column.key}
                        direction={sort?.key === column.key ? sort.direction : undefined}
                      />
                    </button>
                  ) : (
                    column.label
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {result.pageRows.length === 0 ? (
              <tr>
                <td
                  className="px-3 py-8 text-center text-sm text-muted-foreground"
                  colSpan={props.columns.length}
                >
                  {props.emptyMessage ?? "No rows to display."}
                </td>
              </tr>
            ) : (
              result.pageRows.map((row) => (
                <tr
                  className={cn(
                    "border-b border-border/40 last:border-0",
                    props.onRowClick ? "cursor-pointer" : undefined,
                  )}
                  key={props.rowKey(row)}
                  onClick={
                    props.onRowClick
                      ? (event) => {
                          const target = event.target;
                          if (
                            target instanceof Element &&
                            target.closest("button, a, input, select, textarea")
                          ) {
                            return;
                          }
                          props.onRowClick?.(row);
                        }
                      : undefined
                  }
                >
                  {props.columns.map((column) => (
                    <td
                      key={column.key}
                      className={cn("px-3 py-2 text-foreground", column.className)}
                    >
                      {column.render(row)}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2 border-t border-border/70 px-3 py-2 text-xs text-muted-foreground">
        <p>
          {result.total === 0 ? "0 results" : `${result.start}–${result.end} of ${result.total}`}
        </p>
        <div className="flex items-center gap-1">
          <Button
            disabled={result.page <= 1}
            onClick={() => setPage((current) => Math.max(1, current - 1))}
            size="xs"
            type="button"
            variant="outline"
          >
            Previous
          </Button>
          <Button
            disabled={result.page >= result.pageCount}
            onClick={() => setPage((current) => current + 1)}
            size="xs"
            type="button"
            variant="outline"
          >
            Next
          </Button>
        </div>
      </div>
    </div>
  );
}

function SortIcon(props: { active: boolean; direction?: "asc" | "desc" | undefined }) {
  if (!props.active) return <ChevronsUpDownIcon className="size-3 opacity-50" />;
  if (props.direction === "desc") return <ArrowDownIcon className="size-3" />;
  return <ArrowUpIcon className="size-3" />;
}
